{
    This file is part of the Fresnel Library.
    Copyright (c) 2025 by the FPC & Lazarus teams.

    Pas2js Fresnel interface - Selfie-segmentation API

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}
{$mode objfpc}
{$h+}
{$modeswitch externalclass}
{$modeswitch advancedrecords}

{$DEFINE IMAGE_USEOSC}

// MediaPipe SelfieSegmentation API

unit Fresnel.SelfieSegmentation.Pas2js;

interface

uses
  SysUtils, Types, Math,
  JS, WeborWorker, Web;

type

  TJSSelfieSegmentationResults = class external name 'Object' (TJSObject)
    image : TJSImageBitmap;
    segmentationMask : TJSImageBitmap;
  end;

  TSelfieSegmentationResultsCallback = reference to procedure (aResults : TJSSelfieSegmentationResults);

  TJSSelfieSegmentation = class external name 'SelfieSegmentation' (TJSObject)
    constructor new(aOptions : TJSObject);
    procedure setOptions(aOptions : TJSObject);
    procedure onResults(aCallback : TSelfieSegmentationResultsCallback);
    function initialize : TJSPromise;
    procedure send(aObject : TJSObject);
  end;

  { TFresnelSelfieSegmentation }

  TFresnelSelfieSegmentation = class
  private
    FScript : TJSHTMLScriptElement;
    FModel : TJSSelfieSegmentation;
    FModelSelection : Integer;
    FIsReady : Boolean;
    FOnResults : TSelfieSegmentationResultsCallback;

    procedure DoOnResults(aResults : TJSSelfieSegmentationResults);

  public
    constructor Create(const aBaseURL : String);

    // 0 = portrait (general), 1 = landscape
    procedure LoadModel(aModelSelection : Integer); async;

    function IsReady : Boolean;

    procedure Send(aImage : TJSObject);

    property OnResults : TSelfieSegmentationResultsCallback read FOnResults write FOnResults;
  end;

// utility functions to be later be wrapped in an object with proper pipeline

// aBackground <> nil: composite the person over that image (matting), blurAmount
// applied to it (0 = sharp). aBackground = nil: composite over a blurred copy of
// the camera frame.
function ProcessSegmentationResult(
  results: TJSSelfieSegmentationResults;
  maskThreshold, blurAmount, erosionDilationAmount, edgeEnhancementAmount, featheringAmount : Double;
  aBackground : TJSImageBitmap = nil
  ) : TJSImageBitmap;

procedure ApplyFeathering(context: TJSCanvasRenderingContext2D; amount: Double);
procedure EnhanceEdges(context: TJSCanvasRenderingContext2D; amount: Double);
procedure ApplyThreshold(context: TJSCanvasRenderingContext2D; threshold: Double);
procedure ApplyErosionDilation(context: TJSCanvasRenderingContext2D; amount: Double);

// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------
implementation
// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------

// ---------------
// --------------- TFresnelSelfieSegmentation ---------------
// ---------------

// Create
//
constructor TFresnelSelfieSegmentation.Create(const aBaseURL: String);
var
  lScript : TJSHTMLScriptElement;
begin
  FModelSelection := 1;

  lScript := TJSHTMLScriptElement(document.createElement('script'));
  lScript.onload :=
    function(Event: TEventListenerEvent): boolean
    begin
      FScript := lScript;
      FModel := TJSSelfieSegmentation.new(JS.new([
        'locateFile',
        function (aFile : String) : String
        begin
          Result := aBaseURL + '/' + aFile;
        end
      ]));
      if FModelSelection >= 0 then
        LoadModel(FModelSelection);
    end;
  lScript.src := aBaseURL + '/selfie_segmentation.js';
  document.body.append(lScript);
end;

// LoadModel
//
procedure TFresnelSelfieSegmentation.LoadModel(aModelSelection: Integer);

  function DoLoad : Boolean; async;
  begin
    FModel.setOptions(JS.New(['modelSelection', aModelSelection]));
    FModel.onResults(@DoOnResults);
    // Must await initialize fully: otherwise IsReady flips true immediately and
    // the first send() triggers a SECOND initialization that collides with this
    // one's half-built Emscripten Module (Module.arguments abort).
    AWait(TJSObject, FModel.initialize);
    Result := True;
  end;

begin
  FModelSelection := aModelSelection;
  if FScript = nil then Exit;

  FIsReady := AWait(DoLoad);
end;

// IsReady
//
function TFresnelSelfieSegmentation.IsReady: Boolean;
begin
  Result := (FScript <> nil) and FIsReady;
end;

procedure TFresnelSelfieSegmentation.Send(aImage: TJSObject);
begin
  FModel.send(JS.new(['image', aImage]));
end;

// DoOnResults
//
procedure TFresnelSelfieSegmentation.DoOnResults(aResults: TJSSelfieSegmentationResults);
begin
  if Assigned(FOnResults) then
    FOnResults(aResults);
end;

// ---------------
// --------------- Utility functions ---------------
// ---------------

// Persistent scratch canvases reused across frames (segmentation is serialized,
// one ProcessSegmentationResult call at a time), so we don't allocate ~5 canvases
// + 2D contexts per frame.
var
  gScratchW : Integer = 0;
  gScratchH : Integer = 0;
  gOutCanvas : TJSHTMLOffscreenCanvas;
  gOutCtx : TJSOffscreenCanvasRenderingContext2D;
  gPersonCanvas, gMaskCanvas, gBgCanvas : TJSHTMLCanvasElement;
  gPersonCtx, gMaskCtx, gBgCtx : TJSCanvasRenderingContext2D;
  gThresholdDiv : TJSHTMLElement = nil;
  gThresholdValue : Double = -1;

// EnsureSelfieScratch
//
procedure EnsureSelfieScratch(aWidth, aHeight : Integer);
begin
  if (gOutCanvas <> nil) and (gScratchW = aWidth) and (gScratchH = aHeight) then
    Exit;
  gScratchW := aWidth;
  gScratchH := aHeight;

  gOutCanvas := TJSHTMLOffscreenCanvas.New(aWidth, aHeight);
  gOutCtx := gOutCanvas.getContextAs2DContext('2d');

  gPersonCanvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  gPersonCanvas.width := aWidth; gPersonCanvas.height := aHeight;
  gPersonCtx := TJSCanvasRenderingContext2D(gPersonCanvas.getContext('2d'));

  gMaskCanvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  gMaskCanvas.width := aWidth; gMaskCanvas.height := aHeight;
  gMaskCtx := TJSCanvasRenderingContext2D(gMaskCanvas.getContext('2d'));

  gBgCanvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  gBgCanvas.width := aWidth; gBgCanvas.height := aHeight;
  gBgCtx := TJSCanvasRenderingContext2D(gBgCanvas.getContext('2d'));
end;

// EnsureSelfieThresholdFilter
//
// Inject/update a hidden SVG filter that thresholds the mask alpha on the GPU
// (feComponentTransfer, steep linear ramp centred on aThreshold). Rebuilt only
// when aThreshold changes.
procedure EnsureSelfieThresholdFilter(aThreshold : Double);
const
  cSlope = 12.0; // ramp steepness
var
  lIntercept : Double;
begin
  if (gThresholdDiv <> nil) and (gThresholdValue = aThreshold) then Exit;
  gThresholdValue := aThreshold;
  lIntercept := 0.5 - cSlope * aThreshold;
  if gThresholdDiv = nil then
  begin
    gThresholdDiv := TJSHTMLElement(document.createElement('div'));
    gThresholdDiv.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
    document.body.appendChild(gThresholdDiv);
  end;
  gThresholdDiv.innerHTML :=
    '<svg xmlns="http://www.w3.org/2000/svg">' +
    '<filter id="fresnelSelfieThreshold" color-interpolation-filters="sRGB">' +
    '<feComponentTransfer><feFuncA type="linear" slope="' + FloatToStr(cSlope) +
    '" intercept="' + FloatToStr(lIntercept) + '"/></feComponentTransfer>' +
    '</filter></svg>';
end;

// ProcessSegmentationResult
//
function ProcessSegmentationResult(
  results: TJSSelfieSegmentationResults;
  maskThreshold, blurAmount, erosionDilationAmount, edgeEnhancementAmount, featheringAmount : Double;
  aBackground : TJSImageBitmap
  ) : TJSImageBitmap;
var
  mask: TJSImageBitmap;
  w, h : Integer;
  bgScale, bgW, bgH, bgX, bgY : Double;
begin
  w := results.image.width;
  h := results.image.height;
  EnsureSelfieScratch(w, h);
  EnsureSelfieThresholdFilter(maskThreshold);

  mask := results.segmentationMask;

  gOutCtx.clearRect(0, 0, w, h);

  // Person layer = the full camera frame (reset state, reused canvas).
  gPersonCtx.globalCompositeOperation := 'source-over';
  gPersonCtx.filter := 'none';
  gPersonCtx.clearRect(0, 0, w, h);
  gPersonCtx.drawImage(results.image, 0, 0, w, h);

  // Mask layer.
  gMaskCtx.globalCompositeOperation := 'source-over';
  gMaskCtx.clearRect(0, 0, w, h);
  if (erosionDilationAmount = 0) and (edgeEnhancementAmount = 0) then
  begin
    // Fast GPU path (used in-app): threshold (SVG feFuncA) + optional feather
    // blur, applied in a single drawImage - no per-pixel JS.
    if featheringAmount <> 0 then
      gMaskCtx.filter := 'url(#fresnelSelfieThreshold) blur(' + FloatToStr(featheringAmount) + 'px)'
    else
      gMaskCtx.filter := 'url(#fresnelSelfieThreshold)';
    gMaskCtx.drawImage(mask, 0, 0, w, h);
    gMaskCtx.filter := 'none';
  end
  else
  begin
    // Advanced path (erosion/edge enhancement) - per-pixel JS.
    gMaskCtx.filter := 'none';
    gMaskCtx.drawImage(mask, 0, 0, w, h);
    ApplyThreshold(gMaskCtx, maskThreshold);
    if erosionDilationAmount <> 0 then ApplyErosionDilation(gMaskCtx, erosionDilationAmount);
    if edgeEnhancementAmount <> 0 then EnhanceEdges(gMaskCtx, edgeEnhancementAmount);
    if featheringAmount <> 0 then ApplyFeathering(gMaskCtx, featheringAmount);
  end;

  // Cut the person out by the mask.
  gPersonCtx.globalCompositeOperation := 'destination-in';
  gPersonCtx.drawImage(gMaskCanvas, 0, 0, w, h);
  gPersonCtx.globalCompositeOperation := 'source-over'; // reset for next frame

  // Background layer.
  gBgCtx.globalCompositeOperation := 'source-over';
  gBgCtx.filter := 'none';
  gBgCtx.clearRect(0, 0, w, h);

  if aBackground <> nil then
  begin
    // Background matting: cover-fit the replacement image over the frame.
    if blurAmount <> 0 then
      gBgCtx.filter := 'blur(' + FloatToStr(blurAmount) + 'px)';
    bgScale := Max(w / aBackground.width, h / aBackground.height);
    bgW := aBackground.width * bgScale;
    bgH := aBackground.height * bgScale;
    bgX := (w - bgW) / 2;
    bgY := (h - bgH) / 2;
    gBgCtx.drawImage(aBackground, bgX, bgY, bgW, bgH);
    gBgCtx.filter := 'none';
  end
  else if blurAmount <> 0 then
  begin
    // Blur the frame with the person removed (avoids the silhouette bleeding
    // outward into a halo), over an opaque blurred full-frame base that fills the
    // person-shaped hole. Person-excluded frame is built in gOutCanvas (scratch).
    gOutCtx.globalCompositeOperation := 'source-over';
    gOutCtx.filter := 'none';
    gOutCtx.clearRect(0, 0, w, h);
    gOutCtx.drawImage(results.image, 0, 0, w, h);
    gOutCtx.globalCompositeOperation := 'destination-out';
    gOutCtx.drawImage(gMaskCanvas, 0, 0, w, h);
    gOutCtx.globalCompositeOperation := 'source-over';

    gBgCtx.filter := 'blur(' + FloatToStr(blurAmount) + 'px)';
    gBgCtx.drawImage(results.image, 0, 0, w, h); // opaque base: fills the hole
    gBgCtx.drawImage(gOutCanvas, 0, 0, w, h);    // person-excluded, blurred, on top
    gBgCtx.filter := 'none';

    gOutCtx.clearRect(0, 0, w, h); // done using gOutCanvas as scratch
  end
  else
    // No blur, no replacement: sharp camera frame.
    gBgCtx.drawImage(results.image, 0, 0, w, h);

  gOutCtx.globalCompositeOperation := 'source-over';
  gOutCtx.filter := 'none';
  gOutCtx.clearRect(0, 0, w, h);
  gOutCtx.drawImage(gBgCanvas, 0, 0);
  gOutCtx.drawImage(gPersonCanvas, 0, 0);

  Result := gOutCanvas.transferToImageBitmap;
end;

// ApplyFeathering
//
procedure ApplyFeathering(context: TJSCanvasRenderingContext2D; amount: Double);
var
  tempCanvas: TJSHTMLCanvasElement;
  tempCtx: TJSCanvasRenderingContext2D;
begin
  context.filter := 'blur(' + FloatToStr(amount) + 'px)';

  tempCanvas := TJSHTMLCanvasElement(document.createElement('canvas'));
  tempCanvas.width := context.canvas.width;
  tempCanvas.height := context.canvas.height;

  tempCtx := TJSCanvasRenderingContext2D(tempCanvas.getContext('2d'));
  tempCtx.drawImage(context.canvas, 0, 0);

  context.clearRect(0, 0, tempCanvas.width, tempCanvas.height);
  context.drawImage(tempCanvas, 0, 0);
  context.filter := 'none';
end;

// EnhanceEdges
//
procedure EnhanceEdges(context: TJSCanvasRenderingContext2D; amount: Double);
var
  imageData: TJSImageData;
  data: TJSUint8ClampedArray;
  width, height: Integer;
  factor: Double;
  original, edges: TJSUint8ClampedArray;
  i, x, y: Integer;
  center, left, right, top, bottom: Integer;
  enhancedAlpha: Double;
begin
  width := context.canvas.width;
  height := context.canvas.height;
  imageData := context.getImageData(0, 0, width, height);
  data := imageData.data;
  factor := amount * 0.5;

  original := TJSUint8ClampedArray.new(data);
  edges := TJSUint8ClampedArray.new(data.length);

  for y := 1 to height - 2 do begin
    for x := 1 to width - 2 do begin
      i := (y * width + x) * 4 + 3;

      center := original[i];
      left := original[(y * width + (x-1)) * 4 + 3];
      right := original[(y * width + (x+1)) * 4 + 3];
      top := original[((y-1) * width + x) * 4 + 3];
      bottom := original[((y+1) * width + x) * 4 + 3];

      edges[i] := Max(
        Abs(center - left),
        Max(Abs(center - right),
        Max(Abs(center - top),
        Abs(center - bottom)))
      );
    end;
  end;

  i := 3;
  while i < data.length do begin
    if edges[i] > 10 then begin
      enhancedAlpha := original[i] + (edges[i] * factor);
      data[i] := Min(255, Trunc(enhancedAlpha));
    end;
    Inc(i, 4);
  end;

  context.putImageData(imageData, 0, 0);
end;

// ApplyThreshold
//
procedure ApplyThreshold(context: TJSCanvasRenderingContext2D; threshold: Double);
var
  imageData: TJSImageData;
  data: TJSUint8ClampedArray;
  i, width, height: Integer;
begin
  width := context.canvas.width;
  height := context.canvas.height;
  imageData := context.getImageData(0, 0, width, height);
  data := imageData.data;

  i := 3;
  while i < data.length do begin
    if data[i] / 255 < threshold then
      data[i] := 0
    else
      data[i] := 255;

    Inc(i, 4);
  end;

  context.putImageData(imageData, 0, 0);
end;

// ApplyErosionDilation
//
procedure ApplyErosionDilation(context: TJSCanvasRenderingContext2D; amount: Double);
var
  width, height: Integer;
  imageData: TJSImageData;
  data, output: TJSUint8ClampedArray;
  kernelSize: Integer;
  isErosion: Boolean;
  x, y, kx, ky, pixelIndex: Integer;
  targetX, targetY, targetIndex: Integer;
  minValue, maxValue: Integer;
  newImageData: TJSImageData;
begin
  width := context.canvas.width;
  height := context.canvas.height;
  imageData := context.getImageData(0, 0, width, height);
  data := imageData.data;
  output := TJSUint8ClampedArray.new(data);

  kernelSize := Max(1, Trunc(amount/2));
  isErosion := amount < 0;

  for y := 0 to height - 1 do begin
    for x := 0 to width - 1 do begin
      pixelIndex := (y * width + x) * 4 + 3;

      if isErosion then begin
        minValue := 255;
        for ky := -kernelSize to kernelSize do begin
          for kx := -kernelSize to kernelSize do begin
            targetY := Min(height - 1, Max(0, y + ky));
            targetX := Min(width - 1, Max(0, x + kx));
            targetIndex := (targetY * width + targetX) * 4 + 3;
            minValue := Min(minValue, data[targetIndex]);
          end;
        end;
        output[pixelIndex] := minValue;
      end else begin
        maxValue := 0;
        for ky := -kernelSize to kernelSize do begin
          for kx := -kernelSize to kernelSize do begin
            targetY := Min(height - 1, Max(0, y + ky));
            targetX := Min(width - 1, Max(0, x + kx));
            targetIndex := (targetY * width + targetX) * 4 + 3;
            maxValue := Max(maxValue, data[targetIndex]);
          end;
        end;
        output[pixelIndex] := maxValue;
      end;
    end;
  end;

  newImageData := TJSImageData.new(output, width, height);
  context.putImageData(newImageData, 0, 0);
end;


end.
